import {
  getArdoqErrorTraceId,
  isArdoqError,
  toArdoqError,
  type ArdoqError,
} from '@ardoq/common-helpers';
import type { I18nLocale } from '@ardoq/i18n';
import type { Locale } from '@ardoq/locale';
import { confirm, modal } from '@ardoq/modal';
import type { BulkEditOrDeleteCommands } from '../../types';
import { getBulkDeleteFailedDialog } from '../confirmDialogs';
import { BulkDeleteComponentsDialog } from './BulkDeleteComponentsDialog';

/**
 * How the confirmed delete ended. A cancel resolves the modal with `false`.
 */
type BulkDeleteOutcome =
  | { status: 'deleted' }
  | { status: 'failed'; error: ArdoqError };

const showFailure = (i18nLocale: I18nLocale, error: ArdoqError) =>
  confirm(
    getBulkDeleteFailedDialog(i18nLocale, error, getArdoqErrorTraceId(error))
  );

export const openBulkDeleteComponentsDialog = async ({
  i18nLocale,
  locale,
  commands,
  refreshServerSideForGridCallback,
}: {
  i18nLocale: I18nLocale;
  locale: Locale;
  commands: BulkEditOrDeleteCommands;
  refreshServerSideForGridCallback: () => void;
}): Promise<void> => {
  try {
    let isDeleting = false;

    const outcome = await modal<BulkDeleteOutcome>(
      resolve => (
        <BulkDeleteComponentsDialog
          getBulkDeleteComponentCount={commands.getBulkDeleteComponentCount}
          i18nLocale={i18nLocale}
          locale={locale}
          onCancel={() => resolve(false)}
          onConfirm={async counts => {
            isDeleting = true;
            const result = await commands.executeBulkDelete({
              numberOfComponents: counts.deletable,
            });
            resolve(
              isArdoqError(result)
                ? { status: 'failed', error: result }
                : { status: 'deleted' }
            );
          }}
        />
      ),
      // Once the delete is under way the dialog is the only thing reporting
      // it, so a stray Escape or backdrop click must not take it away.
      {
        shouldCloseOnBackdropClick: () => !isDeleting,
        shouldCloseOnEscapeKey: () => !isDeleting,
      }
    );

    if (!outcome) {
      return;
    }
    if (outcome.status === 'failed') {
      showFailure(i18nLocale, outcome.error);
      return;
    }
    refreshServerSideForGridCallback();
  } catch (error) {
    showFailure(i18nLocale, toArdoqError({ error: error as Error }));
  }
};
